import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Star, Rocket, Tag, Truck, ShieldCheck, CreditCard, AlertTriangle, MapPin, ShoppingBag, Package, BadgeCheck } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { REPORT_REASONS, BOOST_PACKAGES, COURIERS, ISLANDS, ISLAND_NAME } from "../lib/data";
import { GMAPS_KEY, loadGoogleMaps, parseAddressComponents } from "../lib/googlePlaces";
import { formatPrice } from "../lib/format";
import { toast } from "sonner";

export function FavoriteOfferModal({ open, onOpenChange, listing, onDone }) {
  const { t } = useI18n();
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (open && listing) {
      setAmount(""); setMessage(""); setPreview(null);
      api.get(`/listings/${listing.id}/favorite-offer-preview`).then(({ data }) => setPreview(data)).catch(() => {});
    }
  }, [open, listing]);

  const submit = async () => {
    if (!amount) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/offer-to-favorites`, { amount: Number(amount), message });
      toast.success(t("favOffer.sent").replace("{n}", data.sent));
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const newCount = preview?.new_recipients ?? 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="fav-offer-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <span className="h-8 w-8 grid place-items-center rounded-lg bg-rose-500 text-white"><Tag className="h-4 w-4" /></span>
            {t("favOffer.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className={`p-3 rounded-xl text-sm ${newCount > 0 ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`} data-testid="fav-offer-preview">
            {preview == null ? t("common.loading")
              : newCount > 0 ? <><b>{newCount}</b> {t("favOffer.newInfo")}{preview.already_sent > 0 ? ` · ${preview.already_sent} ${t("favOffer.alreadySent")}` : ""}</>
              : t("favOffer.noNew")}
          </div>
          <div><Label>{t("favOffer.amount")}</Label>
            <Input data-testid="fav-offer-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" /></div>
          <div><Label>{t("favOffer.message")}</Label>
            <Textarea data-testid="fav-offer-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={2} className="mt-1" /></div>
          <Button data-testid="fav-offer-submit" onClick={submit} disabled={loading || !amount || newCount === 0} className="w-full bg-[#008080] hover:bg-[#006666]">
            {t("favOffer.send")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BoostModal({ open, onOpenChange, listing, onDone }) {
  const { t, lang } = useI18n();
  const [days, setDays] = useState(0);
  const [pay, setPay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paypalOn, setPaypalOn] = useState(false);
  const pkg = BOOST_PACKAGES.find((p) => p.days === days);

  React.useEffect(() => {
    if (open) api.get("/payments/providers").then(({ data }) => setPaypalOn(!!data.paypal)).catch(() => {});
  }, [open]);

  const reset = () => { setDays(0); setPay(false); };
  const confirm = async (provider = "stripe") => {
    if (!days) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/boost`, { days, provider, origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      onOpenChange(false); reset(); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="rounded-2xl" data-testid="boost-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <span className="h-8 w-8 grid place-items-center rounded-lg bg-[#FF9F1C] text-white"><Rocket className="h-4 w-4" /></span>
            {t("sell.boostTitle")}
          </DialogTitle>
        </DialogHeader>
        {!pay ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">{t("sell.boostLabel")} {formatPrice(1.99, lang)}</p>
            <div className="grid grid-cols-2 gap-2">
              {BOOST_PACKAGES.map((p) => (
                <button key={p.days} data-testid={`boost-modal-pkg-${p.days}`} onClick={() => setDays(p.days)}
                  className={`px-3 py-3 rounded-xl border text-left transition-colors ${days === p.days ? "border-[#FF9F1C] bg-amber-50 ring-1 ring-[#FF9F1C]/40" : "border-slate-200 hover:bg-slate-50"}`}>
                  <span className="font-semibold text-sm">{t(`monetization.boosts.${p.key}`)}</span>
                  <span className="block text-xs font-mono-badge text-[#e88f13] mt-0.5">{formatPrice(p.price, lang)}</span>
                </button>
              ))}
            </div>
            <Button data-testid="boost-modal-continue" onClick={() => setPay(true)} disabled={!days} className="w-full bg-[#FF9F1C] hover:bg-[#e88f13] text-white">
              {days ? `${t("dashboard.promote")} · ${formatPrice(pkg?.price || 0, lang)}` : t("dashboard.promote")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-amber-50 border border-[#FF9F1C]/30">
              <span className="font-medium">{t("sell.boostTitle")} · {t(`monetization.boosts.${pkg.key}`)}</span>
              <span className="font-display font-extrabold text-xl">{formatPrice(pkg.price, lang)}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
              <span className="font-mono-badge px-2 py-0.5 rounded bg-slate-200 text-slate-600">Stripe</span>
              <span>{lang === "it" ? "Pagamento con carta o Bizum" : lang === "en" ? "Pay by card or Bizum" : "Pago con tarjeta o Bizum"}</span>
            </div>
            <Button data-testid="boost-modal-confirm" onClick={() => confirm("stripe")} disabled={loading} className="w-full h-11 bg-[#008080] hover:bg-[#006666]">
              {loading ? t("common.loading") : (lang === "it" ? "Paga con carta / Bizum" : lang === "en" ? "Pay by card / Bizum" : "Pagar con tarjeta / Bizum")}
            </Button>
            {paypalOn && (
              <Button data-testid="boost-modal-confirm-paypal" onClick={() => confirm("paypal")} disabled={loading} className="w-full h-11 bg-[#ffc439] hover:bg-[#f0b90b] text-[#003087] font-bold">
                PayPal
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


export function OfferModal({ open, onOpenChange, listing, onDone }) {
  const { t } = useI18n();
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!amount) return;
    setLoading(true);
    try {
      await api.post("/offers", { listing_id: listing.id, amount: Number(amount), message });
      toast.success(t("offer.sent"));
      onOpenChange(false); setAmount(""); setMessage(""); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle className="font-display">{t("offer.title")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>{t("offer.yourOffer")}</Label>
            <Input data-testid="offer-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="120" className="mt-1" /></div>
          <div><Label>{t("offer.messageOpt")}</Label>
            <Textarea data-testid="offer-message-input" value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1" rows={3} /></div>
          <Button data-testid="offer-submit-btn" onClick={submit} disabled={loading || !amount} className="w-full bg-[#008080] hover:bg-[#006666]">{t("offer.send")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SwapModal({ open, onOpenChange, listing, onDone }) {
  const { t } = useI18n();
  const [item, setItem] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!item) return;
    setLoading(true);
    try {
      await api.post("/swaps", { listing_id: listing.id, offered_item: item, message });
      toast.success(t("swap.sent"));
      onOpenChange(false); setItem(""); setMessage(""); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle className="font-display">{t("swap.title")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>{t("swap.yourItem")}</Label>
            <Input data-testid="swap-item-input" value={item} onChange={(e) => setItem(e.target.value)} placeholder={t("swap.itemPh")} className="mt-1" /></div>
          <div><Label>{t("swap.messageOpt")}</Label>
            <Textarea data-testid="swap-message-input" value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1" rows={3} /></div>
          <Button data-testid="swap-submit-btn" onClick={submit} disabled={loading || !item} className="w-full bg-[#2EC4B6] hover:bg-[#25a99d]">{t("swap.send")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ReportModal({ open, onOpenChange, listing }) {
  const { t } = useI18n();
  const [reason, setReason] = useState(REPORT_REASONS[0]);
  const [comment, setComment] = useState("");
  const submit = async () => {
    try {
      await api.post("/reports", { listing_id: listing.id, reason, comment });
      toast.success(t("report.sent"));
      onOpenChange(false); setComment("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle className="font-display">{t("report.title")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>{t("report.reason")}</Label>
            <div className="grid gap-2 mt-2">
              {REPORT_REASONS.map((r) => (
                <button key={r} data-testid={`report-reason-${r}`} onClick={() => setReason(r)}
                  className={`text-left text-sm px-3 py-2 rounded-lg border transition-colors ${reason === r ? "border-rose-400 bg-rose-50 text-rose-700" : "border-slate-200 hover:bg-slate-50"}`}>
                  {t(`report.reasons.${r}`)}
                </button>
              ))}
            </div>
          </div>
          <Textarea data-testid="report-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("report.comment")} rows={2} />
          <Button data-testid="report-submit-btn" onClick={submit} variant="destructive" className="w-full">{t("report.submit")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StarInput({ value, onChange, label }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-600">{label}</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)}>
            <Star className={`h-5 w-5 ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

export function ReviewModal({ open, onOpenChange, listing, onDone }) {
  const { t } = useI18n();
  const [scores, setScores] = useState({ comunicacion: 5, rapidez: 5, producto: 5, trato: 5 });
  const [comment, setComment] = useState("");
  const set = (k, v) => setScores((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    const rating = Math.round((scores.comunicacion + scores.rapidez + scores.producto + scores.trato) / 4);
    try {
      await api.post("/reviews", { listing_id: listing.id, seller_id: listing.seller_id, rating, ...scores, comment });
      toast.success(t("review.sent"));
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle className="font-display">{t("review.title")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {["comunicacion", "rapidez", "producto", "trato"].map((k) => (
            <StarInput key={k} label={t(`review.${k}`)} value={scores[k]} onChange={(v) => set(k, v)} />
          ))}
          <Textarea data-testid="review-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("review.comment")} rows={2} />
          <Button data-testid="review-submit-btn" onClick={submit} className="w-full bg-[#008080] hover:bg-[#006666]">{t("review.submit")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export function OrderReviewModal({ open, onOpenChange, order, onDone }) {
  const { t } = useI18n();
  const [scores, setScores] = useState({ comunicacion: 5, rapidez: 5, producto: 5, trato: 5 });
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const set = (k, v) => setScores((s) => ({ ...s, [k]: v }));
  const other = order?.is_buyer ? order?.seller?.name : order?.buyer?.name;
  const submit = async () => {
    if (!comment.trim()) { toast.error(t("orderReview.commentRequired")); return; }
    setSending(true);
    try {
      await api.post(`/orders/${order.id}/review`, { ...scores, comment: comment.trim() });
      toast.success(t("review.sent"));
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="order-review-modal">
        <DialogHeader><DialogTitle className="font-display">{t("orderReview.title")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {other && <p className="text-sm text-slate-500">{t("orderReview.reviewing")} <b className="text-slate-700">{other}</b></p>}
          {["comunicacion", "rapidez", "producto", "trato"].map((k) => (
            <StarInput key={k} label={t(`review.${k}`)} value={scores[k]} onChange={(v) => set(k, v)} />
          ))}
          <div>
            <Textarea data-testid="order-review-comment" value={comment} maxLength={150}
              onChange={(e) => setComment(e.target.value)} placeholder={t("orderReview.commentPlaceholder")} rows={3} />
            <p className={`text-[11px] mt-1 text-right ${comment.length >= 150 ? "text-rose-500" : "text-slate-400"}`}>{comment.length}/150</p>
          </div>
          <Button data-testid="order-review-submit" onClick={submit} disabled={sending} className="w-full bg-[#008080] hover:bg-[#006666]">{t("review.submit")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}



export function ShipModal({ open, onOpenChange, order, onDone }) {
  const { t } = useI18n();
  const [carrier, setCarrier] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { if (open) { setCarrier(""); setCarrierName(""); setCode(""); } }, [open]);

  const canSubmit = carrier && code.trim() && (carrier !== "otro" || carrierName.trim());
  const submit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post(`/orders/${order.id}/ship`, { carrier, carrier_name: carrierName, tracking_code: code.trim() });
      toast.success(t("orders.shipped"));
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="ship-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <span className="h-8 w-8 grid place-items-center rounded-lg bg-[#008080] text-white"><Truck className="h-4 w-4" /></span>
            {t("orders.markShipped")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-500">{t("orders.shipHint")}</p>
          <div>
            <Label>{t("orders.carrier")} <span className="text-rose-500">*</span></Label>
            <select data-testid="ship-carrier-select" value={carrier} onChange={(e) => setCarrier(e.target.value)}
              className="mt-1 w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-teal-500/40">
              <option value="">—</option>
              {COURIERS.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {carrier === "otro" && (
            <div><Label>{t("orders.carrierName")} <span className="text-rose-500">*</span></Label>
              <Input data-testid="ship-carrier-name" value={carrierName} onChange={(e) => setCarrierName(e.target.value)} className="mt-1" /></div>
          )}
          <div><Label>{t("orders.trackingCode")} <span className="text-rose-500">*</span></Label>
            <Input data-testid="ship-tracking-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="ES123456789" className="mt-1" /></div>
          <Button data-testid="ship-submit-btn" onClick={submit} disabled={loading || !canSubmit} className="w-full h-11 bg-[#008080] hover:bg-[#006666]">
            {loading ? t("common.loading") : t("orders.markShipped")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StarRow({ value, onChange, label }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-600">{label}</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)}>
            <Star className={`h-5 w-5 ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

// Different-island buyer → opens an in-page modal to ask the seller for a shipping quote (pre-filled).
export function ShippingQuoteModal({ open, onOpenChange, listing, onSent }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  React.useEffect(() => {
    if (open && listing) {
      const place = [ISLAND_NAME?.[user?.island] || "", user?.municipality || ""].filter(Boolean).join(", ");
      setMsg(t("shippingQuote.message").replace("{place}", place || "…"));
    }
  }, [open, listing, user, t]);

  if (!listing) return null;
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/messages", { listing_id: listing.id, receiver_id: listing.seller_id, text: msg });
      toast.success(t("chat.sent"));
      onOpenChange(false);
      onSent?.(data?.conversation_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="shipping-quote-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <span className="h-8 w-8 grid place-items-center rounded-lg bg-[#008080] text-white"><Truck className="h-4 w-4" /></span>
            {t("shippingQuote.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("shippingQuote.desc")}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-500">{t("shippingQuote.desc")}</p>
        <Textarea data-testid="shipping-quote-text" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} className="mt-1" />
        <Button data-testid="shipping-quote-send" onClick={send} disabled={sending || !msg.trim()} className="w-full h-11 bg-[#008080] hover:bg-[#006666]">
          {sending ? t("common.loading") : t("shippingQuote.send")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

// Keep the focused input visible above the mobile keyboard.
const scrollFieldIntoView = (e) => { const el = e.target; setTimeout(() => el.scrollIntoView({ block: "center", behavior: "smooth" }), 300); };

// Stable (module-level) text field so typing never remounts the input / loses focus.
function TxtField({ label, value, onChange, testid, type = "text", full = false }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={testid} type={type} value={value} autoComplete="off"
        onChange={(e) => onChange(e.target.value)} onFocus={scrollFieldIntoView} className="mt-1 h-10" />
    </div>
  );
}

// Google Places address autocomplete with graceful fallback + mobile-safe in-flow dropdown.
function AddressAutocomplete({ value, onChange, onSelect, label, testid = "purchase-address" }) {
  const [preds, setPreds] = React.useState([]);
  const [ready, setReady] = React.useState(false);
  const svcRef = React.useRef(null);
  const placesRef = React.useRef(null);
  const tokenRef = React.useRef(null);
  const debRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const enabled = !!GMAPS_KEY;

  React.useEffect(() => {
    if (!enabled) return;
    let alive = true;
    loadGoogleMaps().then((g) => {
      if (!alive) return;
      svcRef.current = new g.maps.places.AutocompleteService();
      placesRef.current = new g.maps.places.PlacesService(document.createElement("div"));
      setReady(true);
    }).catch(() => {});
    return () => { alive = false; };
  }, [enabled]);

  const runQuery = (text) => {
    if (!ready || !svcRef.current || text.trim().length < 3) { setPreds([]); return; }
    if (!tokenRef.current && window.google) tokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    svcRef.current.getPlacePredictions(
      { input: text, componentRestrictions: { country: "es" }, sessionToken: tokenRef.current, types: ["address"] },
      (res) => setPreds(res || []),
    );
  };

  const handleChange = (text) => {
    onChange(text);
    if (!enabled) return;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => runQuery(text), 300);
  };

  const pick = (p) => {
    setPreds([]);
    onChange(p.structured_formatting?.main_text || p.description || "");
    if (!placesRef.current) return;
    placesRef.current.getDetails(
      { placeId: p.place_id, fields: ["address_components"], sessionToken: tokenRef.current },
      (place, status) => {
        tokenRef.current = null;
        if (status !== "OK" || !place?.address_components) return;
        onSelect(parseAddressComponents(place.address_components));
      },
    );
  };

  return (
    <div className="col-span-2 relative">
      <Label className="text-xs">{label}</Label>
      <Input
        ref={inputRef}
        data-testid={testid}
        autoComplete="off"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: "center", behavior: "smooth" }), 300)}
        onBlur={() => setTimeout(() => setPreds([]), 150)}
        className="mt-1 h-10"
      />
      {preds.length > 0 && (
        <ul data-testid="purchase-address-suggestions" className="mt-1 rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          {preds.slice(0, 5).map((p) => (
            <li key={p.place_id}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-start gap-2">
                <MapPin className="h-3.5 w-3.5 text-[#008080] mt-0.5 shrink-0" />
                <span className="min-w-0">
                  <span className="block font-medium truncate">{p.structured_formatting?.main_text || p.description}</span>
                  {p.structured_formatting?.secondary_text && <span className="block text-xs text-slate-400 truncate">{p.structured_formatting.secondary_text}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// Post-sale reciprocal review (buyer↔seller).
export function SaleReviewModal({ open, onOpenChange, sale, onDone }) {
  const { t } = useI18n();
  const [scores, setScores] = useState({ comunicacion: 5, rapidez: 5, producto: 5, trato: 5 });
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const set = (k, v) => setScores((s) => ({ ...s, [k]: v }));

  React.useEffect(() => { if (open) { setScores({ comunicacion: 5, rapidez: 5, producto: 5, trato: 5 }); setComment(""); } }, [open]);

  if (!sale) return null;
  const submit = async () => {
    const rating = Math.round((scores.comunicacion + scores.rapidez + scores.producto + scores.trato) / 4);
    setSending(true);
    try {
      await api.post("/sales/review", { sale_id: sale.sale_id, rating, ...scores, comment });
      toast.success(t("saleReview.done"));
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="sale-review-modal">
        <DialogHeader><DialogTitle className="font-display">{t("saleReview.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("saleReview.reviewing")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {sale.reviewee_name && <p className="text-sm text-slate-500">{t("saleReview.reviewing")} <b className="text-slate-700">{sale.reviewee_name}</b></p>}
          {["comunicacion", "rapidez", "producto", "trato"].map((k) => (
            <StarRow key={k} label={t(`review.${k}`)} value={scores[k]} onChange={(v) => set(k, v)} />
          ))}
          <Textarea data-testid="sale-review-comment" value={comment} maxLength={150} onChange={(e) => setComment(e.target.value)} placeholder={t("review.comment")} rows={2} />
          <Button data-testid="sale-review-submit" onClick={submit} disabled={sending} className="w-full bg-[#008080] hover:bg-[#006666]">{t("review.submit")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


const DISPUTE_REASONS = ["not_received", "not_as_described", "damaged", "delay", "other"];

export function DisputeModal({ open, onOpenChange, order, onDone }) {
  const { t } = useI18n();
  const [reason, setReason] = useState(DISPUTE_REASONS[0]);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/orders/${order.id}/dispute`, { reason, comment });
      toast.success(t("dispute.sent"));
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="dispute-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <span className="h-8 w-8 grid place-items-center rounded-lg bg-rose-500 text-white"><AlertTriangle className="h-4 w-4" /></span>
            {t("dispute.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-500">{t("dispute.openInfo")}</p>
          <div>
            <Label>{t("dispute.reason")}</Label>
            <div className="grid gap-2 mt-2">
              {DISPUTE_REASONS.map((r) => (
                <button key={r} data-testid={`dispute-reason-${r}`} onClick={() => setReason(r)}
                  className={`text-left text-sm px-3 py-2 rounded-lg border transition-colors ${reason === r ? "border-rose-400 bg-rose-50 text-rose-700" : "border-slate-200 hover:bg-slate-50"}`}>
                  {t(`dispute.reasons.${r}`)}
                </button>
              ))}
            </div>
          </div>
          <Textarea data-testid="dispute-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("dispute.comment")} rows={3} />
          <Button data-testid="dispute-submit-btn" onClick={submit} disabled={loading} variant="destructive" className="w-full h-11">
            {loading ? t("common.loading") : t("dispute.submit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
